import React, { useEffect, useState } from "react";
import axios from "../axiosSetup";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import "./EditRoleAccessModal.css";
import CustomSelect from "../components/CustomSelect";

/* ================= CONSTANTS ================= */

const ROLES = ["Product Owner", "Product User", "Vocovo Viewer"];

const PAGES = [
  "User Management",
  "Product Management",
  "Customer Pricing Management",
  "Margin Calculator",
  "Customer Order",
  "PO Templates",
];

const PERMISSIONS = [
  "Create",  
  "Update",
  "Delete",
  "Approve",
  "No Access",
];
const PAGE_PERMISSION_RULES = {
  "Product Management": ["Create", "Update", "No Access"],   //  remove Delete, Approve
  "Customer Order": [
    "Create",    
    "Update",
    // "Delete",
    "Approve",
    "No Access",
  ],                        // approve only
  "Margin Calculator": ["Create", "No Access"],   //  remove Update, Delete
  "PO Templates": ["No Access"],   //  remove Delete, Approve
  "User Management": ["No Access"],   //  only read
  "Customer Pricing Management": ["Create", "Update", "Delete", "No Access"], // remove Approve
};

const PAGE_TO_SERVICE = {
  "User Management": "User Management",
  "Product Management": "Product Management",
  "Customer Pricing Management": "Customer Price Management",
  "Margin Calculator": "Margin Calculator Management",
  "Customer Order": "Customer Orders Management",
  "PO Templates": "Customer Quotes Management",
};

const SERVICE_TO_PAGE = {
  "User Management": "User Management",
  "Product Management": "Product Management",
  "Customer Price Management": "Customer Pricing Management",
  "Margin Calculator Management": "Margin Calculator",
  "Customer Orders Management": "Customer Order",
  "Customer Quotes Management": "PO Templates",
};

const uiPermToApi = (p) => p.toUpperCase();

/* ================= COMPONENT ================= */

const EditRoleAccessModal = ({ open, onClose, onUpdated }) => {
  const [selectedRole, setSelectedRole] = useState("");
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [accessMatrix, setAccessMatrix] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const currentPage = PAGES[currentPageIndex];
  const isLast = currentPageIndex === PAGES.length - 1;

  /* ===== FETCH CHECKBOX DATA ===== */
  useEffect(() => {
    if (!open || !selectedRole) return;

    setLoading(true);

    axios
      .get("/authorization", {
        params: { userRole: selectedRole }, // ✅ CORRECT PARAM
      })
   .then((res) => {
  const data = Array.isArray(res.data) ? res.data[0] : null;

  const matrix = {};
  PAGES.forEach((p) => {
    matrix[p] = [];
  });

  data?.services?.forEach(({ service, operations }) => {
    const page = SERVICE_TO_PAGE[service.trim()];
    if (!page) return;

    // Convert backend ops → UI format
    const uiOps = operations
      .filter((op) => op.toUpperCase() !== "READ")
      .map(
        (op) =>
          op.charAt(0).toUpperCase() + op.slice(1).toLowerCase()
      );

    // Get ONLY permissions that are actually rendered as checkboxes
    const visibleCheckboxes = PAGE_PERMISSION_RULES[page] || PERMISSIONS;

    // Remove "No Access" from visible list because backend never sends it
    const realCheckboxes = visibleCheckboxes.filter(
      (p) => p !== "No Access"
    );

    // ✅ Keep only permissions that have a checkbox on UI
    matrix[page] = uiOps.filter((perm) =>
      realCheckboxes.includes(perm)
    );
  });

  setAccessMatrix(matrix);
}) 
    .finally(() => setLoading(false));
  }, [open, selectedRole]);

useEffect(() => {
  if (open) {
    setCurrentPageIndex(0); //always start from User Management
  }
}, [open]);

  /* ===== TOGGLE PERMISSION ===== */
const togglePermission = (perm) => {
  setAccessMatrix((prev) => {
    let curr = prev[currentPage] || [];

    if (perm === "No Access") {
      return {
        ...prev,
        [currentPage]: ["No Access"],
      };
    }

    // remove No Access if selecting something else
    curr = curr.filter((p) => p !== "No Access");

    const updated = curr.includes(perm)
      ? curr.filter((p) => p !== perm)
      : [...curr, perm];

    return {
      ...prev,
      [currentPage]: updated,
    };
  });
};


  /* ===== UPDATE ===== */
  const handleUpdate = async () => {
      const confirmed = window.confirm(
    `Are you sure that if you update this role it will affect all users of the "${selectedRole}" role?`
  );

  if (!confirmed) return;
  try {
    setSaving(true);

    const payload = [
      {
        role: selectedRole,
        services: Object.entries(accessMatrix).map(([page, perms]) => {
        const hasNoAccess = perms.includes("No Access");

        let operations = hasNoAccess
          ? []
          : Array.from(
              new Set([
                ...perms.map(uiPermToApi),
                "READ", // force Read ONLY here
              ])
            );

        return {
          service: PAGE_TO_SERVICE[page],
          operations,
        };
      })

            },
          ];

          await axios.put(
            "/authorization",
            payload,
            {
              params: {
                userRole: selectedRole,   // QUERY PARAM
              },
            }
          );

    onUpdated();
    onClose();
  } finally {
    setSaving(false);
  }
};


  if (!open) return null;

  return (
    <div className="modal-overlay">
      <div className="modal edit-role-modal">
        <div className="edit-role-header">
          <h2>Edit User Role Access</h2>
          <span className="close-icon" onClick={onClose}>✕</span>
        </div>

        <CustomSelect
          value={selectedRole}
          onChange={setSelectedRole}
          options={ROLES.map((r) => ({ label: r, value: r }))}
          placeholder="Select Role"
        />

        {selectedRole && loading && (
          <div style={{ minHeight: "180px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10 }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 30, color: "#f50087" }} spin />} />
            <span style={{ color: "#f50087", fontWeight: 600 }}>Loading</span>
          </div>
        )}

        {selectedRole && !loading && (
          <div className="permission-card">
            <h3>{currentPage}</h3>

            <div className="permission-grid">
  {(PAGE_PERMISSION_RULES[currentPage] || PERMISSIONS).map((perm) => (
    <label key={perm} className="perm-box">
      <input
        type="checkbox"
        checked={accessMatrix[currentPage]?.includes(perm) || false}
        onChange={() => togglePermission(perm)}
      />
      {perm}
    </label>
  ))}
</div>

          </div>
        )}

        {selectedRole && (
          <div className="modal-footer" >
            {currentPageIndex > 0 && (
              <button className="btn-primary" style={{cursor:"pointer"}} onClick={() => setCurrentPageIndex((i) => i - 1)}>
                ← Prev
              </button>
            )}

            {!isLast ? (
              <button className="btn-primary" style={{cursor:"pointer"}} onClick={() => setCurrentPageIndex((i) => i + 1)}>
                Next →
              </button>
            ) : (
              <button className="btn-primary" disabled={saving} onClick={handleUpdate}>
                {saving ? "Loading..." : "Update"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EditRoleAccessModal;
